/**
 * View switcher for the two EHS MIS reports.
 *
 * They used to be two Safety Hub cards, which put the choice at the wrong
 * moment: a user picking a card has to know the difference between a
 * unit-performance workbook and a management pack before they have seen either.
 * One card now opens the reporting area and this switches between the two views,
 * so the choice is made with both in front of you.
 *
 * Each view keeps its OWN route. That matters: the pages hold real state — typed
 * cell values, a period and rate basis, expanded sections — and a refresh, a
 * bookmark or a link shared with a colleague has to land back on the same view.
 * A tab that only changed component state would lose all of that.
 *
 * `onBeforeLeave` lets a view guard the switch. The workbook grid uses it: it can
 * hold unsaved hand-entered values, and navigating away silently would discard
 * them. Return false to cancel.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, Presentation } from 'lucide-react';

export const MIS_VIEWS = [
  {
    id: 'workbook',
    path: '/safety/ehs-mis',
    label: 'Unit performance data',
    hint: 'The monthly workbook grid for the financial year',
    Icon: LayoutGrid,
  },
  {
    id: 'management',
    path: '/safety/ehs-management-mis',
    label: 'Management report',
    hint: 'Executive scorecard and eighteen sections across every module',
    Icon: Presentation,
  },
];

const MisViewSwitcher = ({ active, onBeforeLeave }) => {
  const navigate = useNavigate();

  const go = async (view) => {
    if (view.id === active) return;
    if (onBeforeLeave) {
      const ok = await onBeforeLeave(view);
      if (ok === false) return;
    }
    navigate(view.path);
  };

  return (
    <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 mb-4">
      {MIS_VIEWS.map((view) => {
        const isActive = view.id === active;
        const { Icon } = view;
        return (
          <button
            key={view.id}
            onClick={() => go(view)}
            title={view.hint}
            aria-current={isActive ? 'page' : undefined}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm
                        transition-colors ${isActive
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 hover:bg-gray-50'}`}
          >
            <Icon className="h-4 w-4" />
            {view.label}
          </button>
        );
      })}
    </div>
  );
};

export default MisViewSwitcher;
